import { Menu, MenuItem, Button, Icon } from '@nike/eds';
import React, { FC, useState, useContext, useEffect } from 'react';
import { CaretDown, CaretUp } from '@nike/nike-design-system-icons';
import { NikeI18nContext } from '@nike/i18n-react';
import { useLanguageContext } from '../context/language/context';
import { LanguageCultureMap, Languages } from '../models/translations';
import styles from './layout.module.css';
import { useSponsorContext } from 'context/Sponsor';
import { updateSponsorCulture } from 'services/user-service';
import { AppConfig } from 'utils/constants';
import { useOktaAuth } from '@okta/okta-react';

export const LanguageMenu: FC = () => {
  const [language, setLanguage] = useLanguageContext();
  const { supportedLanguages } = useContext(NikeI18nContext);
  const { authState } = useOktaAuth();

  const { sponsor } = useSponsorContext();

  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const isLanguageMenuOpen = Boolean(languageMenuAnchor);

  useEffect(() => {
    if (sponsor?.culture) {
      let culture = sponsor.culture;
      const supportedLanguageTags = supportedLanguages?.map(l => l.tag) || null;
      if (AppConfig.passplayCultureMapping.has(culture))
        culture = AppConfig.passplayCultureMapping.get(culture) as string;
      if (supportedLanguageTags?.includes(culture)) setLanguage(culture as Languages);
      else {
        const ln = culture.split('-')[0];
        if (supportedLanguageTags?.includes(ln)) setLanguage(ln as Languages);
        else setLanguage('en' as any);
      }
    } else {
      setLanguage('en' as any);
    }
  }, [sponsor?.culture]);

  const handleOpenLanguageMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    isLanguageMenuOpen ? setLanguageMenuAnchor(null) : setLanguageMenuAnchor(event.currentTarget);
  };
  const handleCloseLanguageMenu = () => {
    setLanguageMenuAnchor(null);
  };
  const handleLanguageChange = (lang: Languages) => () => {
    setLanguage(lang);
    handleCloseLanguageMenu();
    if (authState && authState.isAuthenticated)
      updateSponsorCulture(LanguageCultureMap[lang]).then();
  };
  const body = (
    <>
      {supportedLanguages?.map(({ name, tag }) => (
        <MenuItem key={tag} onClick={handleLanguageChange(tag as Languages)}>
          {name}
        </MenuItem>
      ))}
    </>
  );

  return (
    <Menu
      bodySlot={body}
      placement={'bottom-start'}
      isOpen={isLanguageMenuOpen}
      onClickOutside={() => {
        handleCloseLanguageMenu();
      }}
      className={styles.language_menu}
      id="language-menu"
    >
      <Button
        afterSlot={isLanguageMenuOpen ? <CaretUp /> : <CaretDown />}
        variant="ghost"
        onClick={handleOpenLanguageMenu}
        aria-expanded={isLanguageMenuOpen ? 'true' : undefined}
        aria-haspopup="true"
        aria-controls="language-menu"
        size="small"
      >
        <Icon name="Translation" backgroundColor="var(--eds-color-grey-2)" />{' '}
        {supportedLanguages?.find(({ tag }) => tag === language)?.name}
      </Button>
    </Menu>
  );
};
